// src/redux/bookingsSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import {
  getBooking,
  listBookings,
  updateBookingStatus,
} from "../services/bookingsService";

const norm = (value) => String(value == null ? "" : value).trim();

const isGenericNetworkMessage = (message) => {
  const text = norm(message).toLowerCase();

  return (
    text === "load failed" ||
    text === "failed to fetch" ||
    text === "networkerror when attempting to fetch resource." ||
    text === "network request failed" ||
    text === "request failed" ||
    text.includes("load failed") ||
    text.includes("failed to fetch")
  );
};

const errorMessage = (error, fallback, networkFallback = fallback) => {
  const message = norm(error?.message);

  if (!message) return fallback;
  if (isGenericNetworkMessage(message)) return networkFallback;

  return message;
};

const dedupeBookings = (items) => {
  const byId = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const bookingId = norm(item?.bookingId);
    if (!bookingId) return;
    byId.set(bookingId, item);
  });

  return Array.from(byId.values());
};

export const listBookingsAsync = createAsyncThunk(
  "bookings/list",
  async (
    {
      campaignId,
      status = "",
      offerId = "",
      leadId = "",
      cursor = "",
      append = false,
      limit = 25,
    },
    { signal, rejectWithValue },
  ) => {
    try {
      const result = await listBookings(
        {
          campaignId,
          status,
          offerId,
          leadId,
          cursor,
          limit,
        },
        { signal },
      );

      return {
        ...result,
        campaignId,
        status,
        append,
      };
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      return rejectWithValue({
        message: errorMessage(
          error,
          "Bookings could not be loaded.",
          "Could not load booking requests. Check your connection and try again.",
        ),
        status: Number(error?.status || 0),
        code: norm(error?.code),
      });
    }
  },
);

export const getBookingAsync = createAsyncThunk(
  "bookings/get",
  async (bookingId, { signal, rejectWithValue }) => {
    try {
      return await getBooking(bookingId, { signal });
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      return rejectWithValue({
        message: errorMessage(
          error,
          "Booking details could not be loaded.",
          "Could not load booking details. Check your connection and try again.",
        ),
        status: Number(error?.status || 0),
        code: norm(error?.code),
      });
    }
  },
);

export const updateBookingStatusAsync = createAsyncThunk(
  "bookings/updateStatus",
  async (
    { bookingId, status, ownerNotes },
    { signal, rejectWithValue },
  ) => {
    try {
      return await updateBookingStatus(
        bookingId,
        status,
        { ownerNotes, signal },
      );
    } catch (error) {
      if (error?.name === "AbortError") throw error;

      return rejectWithValue({
        bookingId,
        message: errorMessage(
          error,
          "Booking status could not be updated.",
          "Could not update this booking. Check your connection and try again.",
        ),
        status: Number(error?.status || 0),
        code: norm(error?.code),
      });
    }
  },
);

const initialState = {
  campaignId: "",
  statusFilter: "",
  items: [],
  count: 0,
  nextCursor: null,

  listLoading: false,
  loadingMore: false,
  listError: "",

  selectedBooking: null,
  detailLoading: false,
  detailError: "",

  updatingById: {},
  updateErrorById: {},
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    clearBookingsState: () => initialState,

    setBookingStatusFilter(state, action) {
      state.statusFilter = norm(action.payload).toLowerCase();
      state.listError = "";
      state.updateErrorById = {};
    },

    closeBookingDetails(state) {
      state.selectedBooking = null;
      state.detailLoading = false;
      state.detailError = "";
    },

    clearBookingUpdateError(state, action) {
      const bookingId = norm(action.payload);
      if (bookingId) {
        delete state.updateErrorById[bookingId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listBookingsAsync.pending, (state, action) => {
        const append = action.meta.arg?.append === true;

        state.campaignId = norm(action.meta.arg?.campaignId);
        state.statusFilter = norm(
          action.meta.arg?.status,
        ).toLowerCase();
        state.listError = "";

        if (append) {
          state.loadingMore = true;
        } else {
          state.listLoading = true;
          state.loadingMore = false;
          state.items = [];
          state.count = 0;
          state.nextCursor = null;
          state.selectedBooking = null;
          state.detailError = "";
        }
      })
      .addCase(listBookingsAsync.fulfilled, (state, action) => {
        const {
          items,
          count,
          nextCursor,
          campaignId,
          status,
          append,
        } = action.payload;

        state.campaignId = norm(campaignId);
        state.statusFilter = norm(status).toLowerCase();
        state.items = append
          ? dedupeBookings([...state.items, ...items])
          : dedupeBookings(items);
        state.count = Number.isFinite(Number(count))
          ? Number(count)
          : state.items.length;
        state.nextCursor = nextCursor || null;
        state.listLoading = false;
        state.loadingMore = false;
        state.listError = "";
        state.updateErrorById = {};
      })
      .addCase(listBookingsAsync.rejected, (state, action) => {
        if (action.meta.aborted) {
          state.listLoading = false;
          state.loadingMore = false;
          return;
        }

        state.listLoading = false;
        state.loadingMore = false;
        state.listError =
          action.payload?.message ||
          action.error?.message ||
          "Bookings could not be loaded.";
      })

      .addCase(getBookingAsync.pending, (state) => {
        state.detailLoading = true;
        state.detailError = "";
      })
      .addCase(getBookingAsync.fulfilled, (state, action) => {
        state.selectedBooking = action.payload;
        state.detailLoading = false;
        state.detailError = "";

        const index = state.items.findIndex(
          (item) =>
            item.bookingId === action.payload.bookingId,
        );

        if (index >= 0) {
          state.items[index] = action.payload;
        }
      })
      .addCase(getBookingAsync.rejected, (state, action) => {
        if (action.meta.aborted) {
          state.detailLoading = false;
          return;
        }

        state.detailLoading = false;
        state.detailError =
          action.payload?.message ||
          action.error?.message ||
          "Booking details could not be loaded.";
      })

      .addCase(updateBookingStatusAsync.pending, (state, action) => {
        const bookingId = norm(action.meta.arg?.bookingId);
        if (!bookingId) return;

        state.updatingById[bookingId] = true;
        delete state.updateErrorById[bookingId];
      })
      .addCase(updateBookingStatusAsync.fulfilled, (state, action) => {
        const updated = action.payload;
        const bookingId = norm(updated?.bookingId);

        if (bookingId) {
          delete state.updatingById[bookingId];
          delete state.updateErrorById[bookingId];
        }

        const index = state.items.findIndex(
          (item) => item.bookingId === bookingId,
        );

        if (index >= 0) {
          state.items[index] = updated;
        }

        if (
          state.selectedBooking?.bookingId === bookingId
        ) {
          state.selectedBooking = updated;
        }

        if (
          state.statusFilter &&
          updated.status !== state.statusFilter
        ) {
          state.items = state.items.filter(
            (item) => item.bookingId !== bookingId,
          );
          state.count = Math.max(0, state.count - 1);
        }
      })
      .addCase(updateBookingStatusAsync.rejected, (state, action) => {
        const bookingId = norm(
          action.payload?.bookingId ??
            action.meta.arg?.bookingId,
        );

        if (!bookingId) return;

        delete state.updatingById[bookingId];
        state.updateErrorById[bookingId] =
          action.payload?.message ||
          action.error?.message ||
          "Booking status could not be updated.";
      });
  },
});

export const {
  clearBookingsState,
  setBookingStatusFilter,
  closeBookingDetails,
  clearBookingUpdateError,
} = bookingsSlice.actions;

export const selectBookingsState = (state) =>
  state?.bookings || initialState;

export const selectBookingItems = (state) =>
  selectBookingsState(state).items;

export const selectBookingCount = (state) =>
  selectBookingsState(state).count;

export const selectBookingNextCursor = (state) =>
  selectBookingsState(state).nextCursor;

export const selectBookingListLoading = (state) =>
  selectBookingsState(state).listLoading;

export const selectBookingLoadingMore = (state) =>
  selectBookingsState(state).loadingMore;

export const selectBookingListError = (state) =>
  selectBookingsState(state).listError;

export const selectSelectedBooking = (state) =>
  selectBookingsState(state).selectedBooking;

export const selectBookingDetailLoading = (state) =>
  selectBookingsState(state).detailLoading;

export const selectBookingDetailError = (state) =>
  selectBookingsState(state).detailError;

export const selectBookingUpdatingById = (state) =>
  selectBookingsState(state).updatingById;

export const selectBookingUpdateErrorById = (state) =>
  selectBookingsState(state).updateErrorById;

export default bookingsSlice.reducer;
